import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { SlideProps } from '@/components/SlideWizard'
import { useSlideCopy } from '@/lib/i18n'
import { useLocale } from '@/lib/locale'
import { resolveSite } from '@/lib/site'
import outlookLogo from '@/assets/outlook-logo.webp'
import { copy } from './Slide5Sarah.copy'

const ease = [0.16, 1, 0.3, 1] as const

export default function Slide5Sarah(_props: SlideProps) {
  const { isActive } = _props
  const c = useSlideCopy(copy)
  const isAr = useLocale() === 'ar'
  // The 2FA diagram is drawn in physical LTR coordinates (SVG arrows plus
  // absolute offsets), then mirrored as a whole under RTL via `dir-flip`.
  // Text-bearing children carry `dir-flip` again to read normally.
  const textDir = isAr ? 'rtl' : 'ltr'
  const offscreen = isAr ? '-100%' : '100%'
  // AOL is a US-only provider; the Qatar face skips that row.
  const showAol = resolveSite() !== 'qatar'
  const [guideOpen, setGuideOpen] = useState(false)

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 mx-auto max-w-4xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.65, delay: 0.05, ease }}
        className="mb-8 w-full max-w-[560px]"
      >
        <div dir="ltr" className="dir-flip relative h-[172px] overflow-hidden rounded-[28px] bg-white">
          {/* Positioning stays on a direction-neutral wrapper: `start-*` resolves
              against the element's own direction, so the `dir` goes on the inner block. */}
          <div className="absolute start-[18px] top-[42px] w-[160px]">
            <div dir={textDir} className="dir-flip flex flex-col items-start text-start">
              <p className="text-xs font-medium uppercase tracking-[0.16em] text-slate-400">
                {c.passwordLabel}
              </p>
              <div className="mt-2.5 flex h-10 w-[144px] items-center rounded-2xl border border-slate-200 bg-white px-3.5">
                <div className="flex w-full items-center justify-between">
                  {Array.from({ length: 10 }).map((_, index) => (
                    <motion.span
                      key={index}
                      className="text-base font-semibold leading-none text-slate-500"
                      initial={{ opacity: 0, scale: 0.6 }}
                      animate={isActive ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.6 }}
                      transition={{ duration: 0.24, delay: 0.34 + index * 0.1, ease }}
                    >
                      *
                    </motion.span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 10, y: 6 }}
            animate={isActive ? { opacity: 1, x: 0, y: 0 } : { opacity: 0, x: 10, y: 6 }}
            transition={{ duration: 0.58, delay: 2.15, ease }}
            className="absolute start-[232px] top-[20px] h-[112px] w-[132px] overflow-hidden rounded-t-[30px] bg-[#10182b] p-[3px] shadow-[0_16px_36px_rgba(15,23,42,0.12)] ring-1 ring-slate-900/10"
          >
            <div
              dir={textDir}
              className="dir-flip relative h-[calc(100%+3px)] rounded-t-[27px] bg-[#f3f4f6] text-start ring-1 ring-white/70"
            >
              <div className="absolute inset-x-0 top-0 flex items-center justify-between px-3 pt-2 text-[7px] font-medium text-slate-500">
                <span>9:41</span>
                <div className="flex items-center gap-1">
                  <svg className="h-[7px] w-[10px]" viewBox="0 0 10 7" fill="none" aria-hidden="true">
                    <rect x="0.75" y="1" width="6.2" height="5" rx="1" stroke="currentColor" strokeWidth="0.9" />
                    <path d="M7.6 2.4V4.6" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" />
                    <rect x="1.7" y="1.95" width="4.1" height="3.1" rx="0.55" fill="currentColor" />
                  </svg>
                  <svg className="h-[7px] w-[8px]" viewBox="0 0 8 7" fill="currentColor" aria-hidden="true">
                    <rect x="0.5" y="4.5" width="1.1" height="2" rx="0.35" />
                    <rect x="2.7" y="3.3" width="1.1" height="3.2" rx="0.35" />
                    <rect x="4.9" y="2.1" width="1.1" height="4.4" rx="0.35" />
                  </svg>
                </div>
              </div>
              <div className="absolute inset-x-0 top-0 flex justify-center pt-2.5">
                <div className="h-[7px] w-10 rounded-full bg-[#0f172a]" />
              </div>
              <div className="h-full px-3 pt-7">
                <div className="rounded-[15px] bg-slate-100 px-3 py-2.5 text-[12px] leading-snug text-slate-700 shadow-[0_6px_16px_rgba(15,23,42,0.04)]">
                  <motion.p
                    initial={{ opacity: 0, y: 6 }}
                    animate={isActive ? { opacity: 1, y: 0 } : { opacity: 0, y: 6 }}
                    transition={{ duration: 0.48, delay: 2.8, ease }}
                  >
                    {c.smsCode}
                  </motion.p>
                </div>
              </div>
            </div>
          </motion.div>

          <svg className="absolute inset-0 h-full w-full" viewBox="0 0 560 172" fill="none" aria-hidden="true">
            <defs>
              <marker
                id="slide5-2fa-arrow"
                markerWidth="8"
                markerHeight="8"
                refX="6"
                refY="4"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path d="M0 1.5L6 4L0 6.5V1.5Z" fill="#94a3b8" />
              </marker>
            </defs>
            <motion.line
              x1="182"
              y1="86"
              x2="182"
              y2="86"
              stroke="#94a3b8"
              strokeWidth="2"
              strokeDasharray="8 8"
              strokeLinecap="round"
              markerEnd="url(#slide5-2fa-arrow)"
              initial={{ opacity: 0 }}
              animate={isActive ? { x2: 232, opacity: 1 } : { x2: 182, opacity: 0 }}
              transition={{ duration: 0.55, delay: 1.8, ease }}
            />
            <motion.line
              x1="364"
              y1="86"
              x2="364"
              y2="86"
              stroke="#94a3b8"
              strokeWidth="2"
              strokeDasharray="8 8"
              strokeLinecap="round"
              markerEnd="url(#slide5-2fa-arrow)"
              initial={{ opacity: 0 }}
              animate={isActive ? { x2: 448, opacity: 1 } : { x2: 364, opacity: 0 }}
              transition={{ duration: 0.8, delay: 3.45, ease }}
            />
          </svg>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={isActive ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.94 }}
            transition={{ duration: 0.35, delay: 4.3, ease }}
            className="absolute end-[42px] top-[50px] h-[74px] w-[58px]"
          >
            <div className="absolute inset-x-0 bottom-0 h-[48px] rounded-[16px] bg-[#18213A]" />
            <div className="absolute left-1/2 bottom-[20px] h-2.5 w-2.5 -translate-x-1/2 rounded-full bg-white/90" />
            <div className="absolute left-1/2 bottom-[14px] h-2.5 w-1.5 -translate-x-1/2 rounded-full bg-white/90" />
            <motion.div
              initial={{ rotate: 0, x: 0, y: 0 }}
              animate={isActive ? { rotate: [0, 0, -24], x: [0, 0, -4], y: [0, 0, -8] } : { rotate: 0, x: 0, y: 0 }}
              transition={{ duration: 0.72, times: [0, 0.45, 1], delay: 4.72, ease }}
              className="absolute left-1/2 top-[1px] h-[32px] w-[36px] -translate-x-1/2"
            >
              <svg className="h-full w-full text-slate-900" viewBox="0 0 40 40" fill="none" aria-hidden="true">
                <path
                  d="M10 18V12.5C10 8.08172 14.4772 4.5 20 4.5C25.5228 4.5 30 8.08172 30 12.5V18"
                  stroke="currentColor"
                  strokeWidth="4.2"
                  strokeLinecap="round"
                />
              </svg>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isActive ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3, delay: 5.0, ease }}
              className="absolute end-0 top-0 inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-emerald-500 shadow-[0_8px_20px_rgba(16,185,129,0.18)]"
            >
              <svg className="dir-flip h-3 w-3" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <motion.path
                  d="M3 8.25L6.2 11.35L13 4.65"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0 }}
                  animate={isActive ? { pathLength: 1 } : { pathLength: 0 }}
                  transition={{ duration: 0.3, delay: 5.06, ease }}
                />
              </svg>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      <motion.h2
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.15, ease }}
        className="text-4xl sm:text-6xl font-semibold text-slate-900 tracking-tight leading-[1.1] text-balance"
      >
        {c.title}
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.45, ease }}
        className="mt-6 text-xl sm:text-2xl text-slate-500 leading-relaxed max-w-3xl"
      >
        {c.body}
      </motion.p>

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.7, ease }}
        className="mt-6 text-base sm:text-lg text-slate-400 italic leading-relaxed max-w-xl"
      >
        {c.note}
      </motion.p>

      <motion.button
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.95, ease }}
        onClick={() => setGuideOpen(true)}
        className="mt-8 inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-base font-semibold text-slate-700 transition-colors hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 cursor-pointer"
      >
        {c.guideButton}
        <svg className="dir-flip h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
            clipRule="evenodd"
          />
        </svg>
      </motion.button>

      {/* Slideover */}
      <AnimatePresence>
        {guideOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setGuideOpen(false)}
              className="fixed inset-0 z-40 bg-slate-900/40"
            />
            <motion.div
              initial={{ x: offscreen }}
              animate={{ x: 0 }}
              exit={{ x: offscreen }}
              transition={{ duration: 0.35, ease }}
              className="fixed inset-y-0 end-0 z-50 flex w-full max-w-md flex-col overflow-y-auto border-s border-slate-200 bg-white text-start shadow-[0_18px_60px_rgba(15,23,42,0.12)]"
            >
              <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/90 px-6 py-4 backdrop-blur-md">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                    <div>
                      <h3 className="text-lg font-semibold tracking-tight text-slate-900">
                        {c.guideTitle}
                      </h3>
                    </div>
                  </div>
                  <button
                    onClick={() => setGuideOpen(false)}
                    aria-label={c.closeAria}
                    className="flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 cursor-pointer"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="px-6 py-7 sm:px-8">
                <div className="space-y-7">
                  <div className="space-y-5">
                    <section>
                      <h4 className="text-base font-semibold text-slate-900">
                        <span className="text-slate-400 tabular-nums">1.</span>{' '}
                        {c.step1Heading}
                      </h4>
                      <p className="mt-3 text-base leading-relaxed text-slate-600">
                        {c.step1Before}
                        <svg
                          className="inline-block h-[1.05em] w-[1.05em] -translate-y-[1px] align-middle text-slate-500"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" />
                          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
                        </svg>
                        {c.step1After}
                      </p>
                    </section>

                    <section>
                      <h4 className="text-base font-semibold text-slate-900">
                        <span className="text-slate-400 tabular-nums">2.</span>{' '}
                        {c.step2Heading}
                      </h4>
                      <p className="mt-3 text-base leading-relaxed text-slate-600">
                        {c.step2Body}
                      </p>
                    </section>

                    <section>
                      <h4 className="text-base font-semibold text-slate-900">
                        <span className="text-slate-400 tabular-nums">3.</span>{' '}
                        {c.step3Heading}
                      </h4>
                      <p className="mt-3 text-base leading-relaxed text-slate-600">
                        {c.step3Body}
                      </p>
                    </section>
                  </div>

                  <section>
                    <h4 className="text-base font-semibold text-slate-900">
                      {c.emailHeading}
                    </h4>
                    <p className="mt-2 text-base text-slate-600 leading-relaxed">
                      {c.emailBody}
                    </p>

                    <div className="mt-4 space-y-2.5">
                      {/* Gmail */}
                      <a
                        href="https://myaccount.google.com/signinoptions/two-step-verification"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center gap-3.5 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-violet-200 hover:bg-violet-50/30 transition-colors cursor-pointer"
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white">
                          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="#EA4335" aria-hidden="true">
                            <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 010 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" />
                          </svg>
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-base font-semibold text-slate-900">
                            {c.gmailName}
                          </p>
                          <p className="text-sm text-slate-500 leading-snug">
                            {c.gmailDesc}
                          </p>
                        </div>
                        <svg className="dir-flip h-4 w-4 shrink-0 text-slate-300 group-hover:text-violet-500 transition-colors" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                        </svg>
                      </a>

                      {/* Outlook / Hotmail */}
                      <a
                        href="https://account.microsoft.com/security"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center gap-3.5 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-violet-200 hover:bg-violet-50/30 transition-colors cursor-pointer"
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white ring-1 ring-slate-200 overflow-hidden">
                          <img src={outlookLogo} alt="" className="h-7 w-7 object-contain" />
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-base font-semibold text-slate-900">
                            {c.outlookName}
                          </p>
                          <p className="text-sm text-slate-500 leading-snug">
                            {c.outlookDesc}
                          </p>
                        </div>
                        <svg className="dir-flip h-4 w-4 shrink-0 text-slate-300 group-hover:text-violet-500 transition-colors" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                        </svg>
                      </a>

                      {/* Yahoo */}
                      <a
                        href="https://login.yahoo.com/account/security"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center gap-3.5 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-violet-200 hover:bg-violet-50/30 transition-colors cursor-pointer"
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#6001D2]">
                          <span className="text-white font-black text-base leading-none tracking-tight">Y!</span>
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-base font-semibold text-slate-900">
                            {c.yahooName}
                          </p>
                          <p className="text-sm text-slate-500 leading-snug">
                            {c.yahooDesc}
                          </p>
                        </div>
                        <svg className="dir-flip h-4 w-4 shrink-0 text-slate-300 group-hover:text-violet-500 transition-colors" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                        </svg>
                      </a>

                      {/* iCloud */}
                      <a
                        href="https://account.apple.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center gap-3.5 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-violet-200 hover:bg-violet-50/30 transition-colors cursor-pointer"
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white">
                          <svg className="h-6 w-6" viewBox="0 0 24 24" aria-hidden="true">
                            <defs>
                              <linearGradient id="slide5-icloud-grad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0" stopColor="#3A9EEF" />
                                <stop offset="1" stopColor="#5BD4EC" />
                              </linearGradient>
                            </defs>
                            <path fill="url(#slide5-icloud-grad)" d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
                          </svg>
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-base font-semibold text-slate-900">
                            {c.icloudName}
                          </p>
                          <p className="text-sm text-slate-500 leading-snug">
                            {c.icloudDesc}
                          </p>
                        </div>
                        <svg className="dir-flip h-4 w-4 shrink-0 text-slate-300 group-hover:text-violet-500 transition-colors" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                        </svg>
                      </a>

                      {/* AOL (US-only provider, hidden on the Qatar face) */}
                      {showAol && (
                        <a
                          href="https://login.aol.com/account/security"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group flex items-center gap-3.5 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-violet-200 hover:bg-violet-50/30 transition-colors cursor-pointer"
                        >
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-black">
                            <span className="text-white font-bold text-xs tracking-tight">Aol.</span>
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-base font-semibold text-slate-900">
                              {c.aolName}
                            </p>
                            <p className="text-sm text-slate-500 leading-snug">
                              {c.aolDesc}
                            </p>
                          </div>
                          <svg className="dir-flip h-4 w-4 shrink-0 text-slate-300 group-hover:text-violet-500 transition-colors" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                          </svg>
                        </a>
                      )}
                    </div>
                  </section>

                  <div className="pt-1">
                    <p className="text-base text-slate-600 leading-relaxed">
                      {c.thenBanks}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
